import React, { type FC, useEffect, useMemo, useRef, useState } from 'react'

import EmojiPicker, {
  Categories,
  SuggestionMode
} from 'emoji-picker-react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import noCommentsImg from 'assets/images/comment-bg.png'
import { addToast } from 'components/Toast/Toast'
import CommentBlock from 'features/Comment/CommentBlock'
import { AllIcons } from 'helpers/allIcons'
import './_previewComments.scss'
import { useDeleteComment } from 'helpers/hooks'
import { getPostCommentsInfinite, postNewComment, postNewReply } from 'services/endpoints/post'
import { useUserProfile } from 'store'
import { type CommentRow, useCommentsCacheStore } from 'store/commentsCacheStore'
import { usePreviewStore } from 'store/previewStore'

// unwrap axios/service responses shaped like { status, code, data: <row> }
const unwrapRow = (res: any) => (res && typeof res === 'object' && 'data' in res ? res.data : res)

// Keep a stable client key to prevent flicker when temp → real ID swaps
const ensureClientKey = <T extends Record<string, any>>(row: T): T & { __ck: string } => {
  const ck = row?.__ck ?? `ck-${row?.id ?? Math.random().toString(36).slice(2)}`
  return { ...row, __ck: ck }
}

interface ICommentEntity {
  entityId: string
  parentId: string
  owner?: string
  ownerId: string
  isReply: boolean
}

interface Props {
  commentsActive: boolean
  activeCommentsPostId: string
  setCommentsActive: (value: boolean) => void
  commentCount: number
  isMyPost: boolean
  isSingleCommentLink?: boolean
  viewAllCommentsFn?: () => void
  postUserId?: string
}

const PreviewComments: FC<Props> = ({
  commentsActive,
  activeCommentsPostId,
  setCommentsActive,
  commentCount,
  isMyPost,
  isSingleCommentLink = false,
  viewAllCommentsFn
}) => {
  const { t } = useTranslation()
  const userData = useUserProfile()!

  const queryClient = useQueryClient()
  const previewQuery = usePreviewStore(s => s.state.query)

  //  single source of truth for bumping the selected post comment_count
  const bumpSelectedPostCommentCount = usePreviewStore(s => s.bumpSelectedPostCommentCount)

  // UI state
  const [newComment, setNewComment] = useState('')
  const [toggleEmojiPicker, setToggleEmojiPicker] = useState(false)
  const [isCommentReply, setIsCommentReply] = useState(false)
  const [selectedEntity, setSelectedEntity] = useState<ICommentEntity | null>(null)
  const [optionsActive, setOptionsActive] = useState(false)

  // Only re-sync visible count when switching posts
  const [displayCount, setDisplayCount] = useState<number>(commentCount)

  const hasValidPostId = useMemo(
    () =>
      typeof activeCommentsPostId === 'string' &&
      activeCommentsPostId.trim().length > 0,
    [activeCommentsPostId]
  )

  useEffect(() => {
    setDisplayCount(commentCount)
  }, [activeCommentsPostId])

  // Refs
  const inputRef = useRef<HTMLInputElement>(null)
  const commentsContainerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const emojiButtonRef = useRef<HTMLDivElement>(null)

  // Store selectors
  const orderedComments = useCommentsCacheStore(s => (hasValidPostId ? s.getOrdered(activeCommentsPostId) : []))
  const hydratePage = useCommentsCacheStore(s => s.hydratePage)
  const appendOptimistic = useCommentsCacheStore(s => s.appendOptimistic)
  const resolveOptimistic = useCommentsCacheStore(s => s.resolveOptimistic)
  const rollbackOptimistic = useCommentsCacheStore(s => s.rollbackOptimistic)
  const appendReplyOptimistic = useCommentsCacheStore(s => s.appendReplyOptimistic)
  const resolveReply = useCommentsCacheStore(s => s.resolveReply)
  const rollbackReply = useCommentsCacheStore(s => s.rollbackReply)
  const removeCommentFromStore = useCommentsCacheStore(s => s.removeComment)
  const removeReplyFromStore = useCommentsCacheStore(s => s.removeReply)

  const storeSlice = useCommentsCacheStore(s => s.byPostId[String(activeCommentsPostId)])
  const storeHasMore = !!storeSlice?.hasMore
  const storeCurrent = storeSlice?.currentPage ?? 1
  const storeLast = storeSlice?.lastPage ?? 1

  // Paging + control refs
  const currentPageRef = useRef(1)
  const lastPageRef = useRef(1)
  const inFlightRef = useRef(false)
  const loadingPrevRef = useRef(false)

  // Auto-pin logic
  const pinToBottomRef = useRef(true)
  const justPostedRef = useRef(false)
  const lastRenderedCkRef = useRef<string | null>(null)

  // Show "Load previous" only when at top
  const [isAtTop, setIsAtTop] = useState(false)
  const TOP_THRESHOLD = 12 // px

  const scrollToBottom = () => {
    const el = commentsContainerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }


  
  const isNearBottom = () => {
    const el = commentsContainerRef.current
    if (!el) return false
    const threshold = 24 // px
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold
  }

  const handleContainerScroll = () => {
    const el = commentsContainerRef.current
    pinToBottomRef.current = isNearBottom()
    if (el) setIsAtTop(el.scrollTop <= TOP_THRESHOLD)
  }

  const startReply = (params: {
    entityId: string
    parentId: string
    ownerId: string
    owner?: string
    isReply: boolean
  }) => {
    const owner = params.owner?.trim() ?? ''

    setIsCommentReply(true)
    setSelectedEntity({
      entityId: params.entityId,
      parentId: params.parentId,
      owner,
      ownerId: params.ownerId,
      isReply: params.isReply
    })

    setToggleEmojiPicker(false)

    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }

  const closeComments = () => {
  const active = document.activeElement as HTMLElement | null
  const isInputFocused = active === inputRef.current

  if (isInputFocused) {
    active?.blur()

    window.setTimeout(() => {
      setCommentsActive(false)
      setToggleEmojiPicker(false)
      setIsCommentReply(false)
      setSelectedEntity(null)
    }, 250)

    return
  }

  setCommentsActive(false)
  setToggleEmojiPicker(false)
  setIsCommentReply(false)
  setSelectedEntity(null)
}


// KEEP COMMENT COUNT IN SYNC WITH POSTS FEED

const patchPostCommentCount = (postId: string, delta: number) => {
  const queryKey = previewQuery as any
  const cachedData: any = queryClient.getQueryData(queryKey)

  if (!cachedData?.pages) return

  queryClient.setQueryData(queryKey, {
    ...cachedData,
    pages: cachedData.pages.map((singleFeedPage: any) => ({
      ...singleFeedPage,
      page: {
        ...singleFeedPage.page,
        data: {
          ...singleFeedPage.page.data,
          data: singleFeedPage.page.data.data.map((post: any) =>
            post.id === postId
              ? {
                  ...post,
                  comment_count: Math.max(0, (post.comment_count ?? 0) + delta),
                }
              : post
          ),
        },
      },
    })),
  })
}



  // Adjust input padding when replying
  // On open: pin to bottom (cached) and refresh page 1; scroll again after it lands
  useEffect(() => {
    if (!commentsActive) return
    if (!hasValidPostId) return

    pinToBottomRef.current = true
    requestAnimationFrame(() => {
      scrollToBottom()
      setIsAtTop(false)
    })

    if (inFlightRef.current) return
    inFlightRef.current = true

    ;(async () => {
      try {
        const res = await getPostCommentsInfinite(activeCommentsPostId, 1)
        const page = (res?.page?.data?.data as CommentRow[] | undefined)?.map(ensureClientKey) ?? []
        const meta = res?.page?.data?.meta

        hydratePage(activeCommentsPostId, page, meta?.current_page ?? 1, meta?.last_page ?? 1)

        setDisplayCount(commentCount)

        currentPageRef.current = meta?.current_page ?? 1
        lastPageRef.current = meta?.last_page ?? 1

        if (pinToBottomRef.current) {
          requestAnimationFrame(() => {
            scrollToBottom()
            setIsAtTop(false)
          })
        }
      } catch {
        addToast('error', 'Failed to load comments')
      } finally {
        inFlightRef.current = false
      }
    })()
  }, [commentsActive, activeCommentsPostId])

  // Keep pinned to bottom when list changes while pinned (but not after loading previous)
  useEffect(() => {
    if (!commentsActive) return
    if (loadingPrevRef.current) return
    if (!pinToBottomRef.current) return
    if (orderedComments.length > 0) {
      requestAnimationFrame(() => {
        scrollToBottom()
        setIsAtTop(false)
      })
    }
  }, [orderedComments.length, commentsActive])

  // After a new comment/reply is appended and renders, scroll once
  useEffect(() => {
    if (!justPostedRef.current || orderedComments.length === 0) return
    const last: any = orderedComments[orderedComments.length - 1]
    const lastCk = last?.__ck || last?.id
    if (lastCk && lastCk !== lastRenderedCkRef.current) {
      lastRenderedCkRef.current = lastCk
      if (pinToBottomRef.current) {
        requestAnimationFrame(() => {
          scrollToBottom()
          setIsAtTop(false)
        })
      }
      justPostedRef.current = false
    }
  }, [orderedComments])

  // Manual "load previous" (older) — do NOT auto-scroll
  const handleLoadPreviousComments = async () => {
    if (!hasValidPostId) return

    const nextPage = storeCurrent + 1
    if (nextPage > storeLast) return

    const container = commentsContainerRef.current
    const prevHeight = container?.scrollHeight ?? 0
    const prevTop = container?.scrollTop ?? 0

    try {
      loadingPrevRef.current = true

      const res = await getPostCommentsInfinite(activeCommentsPostId, nextPage)
      const page = (res?.page?.data?.data as CommentRow[] | undefined)?.map(ensureClientKey) ?? []
      const meta = res?.page?.data?.meta

      hydratePage(
        activeCommentsPostId,
        page,
        meta?.current_page ?? nextPage,
        meta?.last_page ?? lastPageRef.current
      )

      currentPageRef.current = meta?.current_page ?? nextPage
      lastPageRef.current = meta?.last_page ?? lastPageRef.current

      requestAnimationFrame(() => {
        const el = commentsContainerRef.current
        if (!el) return

        const newHeight = el.scrollHeight
        const delta = newHeight - prevHeight
        let targetTop = prevTop + delta

        const boundaryId = page.at(-1)?.id
        if (boundaryId != null) {
          const boundaryEl = el.querySelector<HTMLElement>(`[data-id="${boundaryId}"]`)
          if (boundaryEl) {
            const half = Math.min(boundaryEl.offsetHeight / 2, 120)
            targetTop = Math.max(0, targetTop - half)
          } else {
            targetTop = Math.max(0, targetTop - 64)
          }
        } else {
          targetTop = Math.max(0, targetTop - 64)
        }

        el.scrollTop = targetTop
        setIsAtTop(el.scrollTop <= TOP_THRESHOLD)
      })
    } catch {
      addToast('error', 'Failed to load more comments')
    } finally {
      loadingPrevRef.current = false
    }
  }

  // ---------- Optimistic add (top-level) ----------
  const newPostComment = useMutation(
    async (comment: string) => {
      if (!hasValidPostId) throw new Error('Missing post id')
      return await postNewComment(activeCommentsPostId, comment)
    },
    {
      onMutate: async (text: string) => {
        if (!hasValidPostId) return

        const tempId = `temp-${Date.now()}`
        const optimisticRow: CommentRow & { __ck: string } = ensureClientKey({
          id: tempId,
          parent_id: null,
          entity_type: 'App\\Models\\Post',
          entity_id: activeCommentsPostId,
          user_id: (userData as any)?.id ?? null,
          post_id: activeCommentsPostId,
          model_id: (userData as any)?.id ?? null,
          comment: text,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user: {
            id: (userData as any)?.id ?? null,
            username: (userData as any)?.username ?? (userData as any)?.display_name ?? 'you',
            display_name: (userData as any)?.display_name ?? (userData as any)?.username ?? 'You',
            avatar: { id: (userData as any)?.avatar?.id, url: (userData as any)?.avatar?.url ?? '' }
          },
          comment_count: 0,
          like_count: 0,
          is_liked: false,
          comments: [],
          __optimistic__: true,
          __pending__: true
        } as any)

        appendOptimistic(activeCommentsPostId, optimisticRow)

        setDisplayCount(c => c + 1)
        bumpSelectedPostCommentCount(+1)
        patchPostCommentCount(activeCommentsPostId, +1)
        setNewComment('')
        setToggleEmojiPicker(false)
        setIsCommentReply(false)
        setSelectedEntity(null)

        justPostedRef.current = true
        pinToBottomRef.current = true

        return { tempId, optimisticRow }
      },
      onSuccess: (serverCommentWrapped: any, _text, ctx) => {
        if (!hasValidPostId) return
        if (!ctx?.tempId) return

        const real = unwrapRow(serverCommentWrapped) as CommentRow
        const merged: CommentRow & { __ck?: string } = {
          ...(ctx.optimisticRow as CommentRow),
          ...(real as any),
          user: (real as any)?.user ? (real as any).user : (ctx.optimisticRow as any).user,
          __ck: (ctx.optimisticRow as any).__ck
        }

        resolveOptimistic(activeCommentsPostId, ctx.tempId, merged)
      },
      onError: (err: any, _text, ctx) => {
        if (!hasValidPostId) return
        if (ctx?.tempId) {
          rollbackOptimistic(activeCommentsPostId, ctx.tempId)
          setDisplayCount(c => Math.max(0, c - 1))
          bumpSelectedPostCommentCount(-1)
        }
        addToast('error', err?.response?.data?.error?.message || 'There was an error')
      }
    }
  )

  // ---------- Optimistic reply ----------
  const commentReply = useMutation(
    async (vars: { id: string; reply: string }) => {
      if (!hasValidPostId) throw new Error('Missing post id')
      return await postNewReply(vars)
    },
    {
      onMutate: async ({ id, reply }) => {
        if (!hasValidPostId) return

        const tempId = `temp-r-${Date.now()}`
        const optimisticReply: CommentRow & { __ck: string } = ensureClientKey({
          id: tempId,
          parent_id: id,
          type: 'App\\Models\\Post',
          entity_id: activeCommentsPostId,
          user_id: (userData as any)?.id ?? null,
          post_id: activeCommentsPostId,
          model_id: (userData as any)?.id ?? null,
          comment: reply,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user: {
            id: (userData as any)?.id ?? null,
            username: (userData as any)?.username ?? (userData as any)?.display_name ?? 'you',
            display_name: (userData as any)?.display_name ?? (userData as any)?.username ?? 'You',
            avatar: { id: (userData as any)?.avatar?.id, url: (userData as any)?.avatar?.url ?? '' }
          },
          comment_count: 0,
          like_count: 0,
          is_liked: false,
          comments: [],
          __optimistic__: true,
          __pending__: true
        } as any)

        appendReplyOptimistic(activeCommentsPostId, id, optimisticReply)

        setDisplayCount(c => c + 1)
        bumpSelectedPostCommentCount(+1)
        patchPostCommentCount(activeCommentsPostId, +1)
        setNewComment('')
        setToggleEmojiPicker(false)
        setIsCommentReply(false)
        setSelectedEntity(null)

        justPostedRef.current = true
        pinToBottomRef.current = true

        return { tempId, parentId: id, optimisticReply }
      },
      onSuccess: (serverReplyWrapped: any, _vars, ctx) => {
        if (!hasValidPostId) return
        if (!ctx?.tempId || !ctx?.parentId) return

        const real = unwrapRow(serverReplyWrapped) as CommentRow
        const merged: CommentRow & { __ck?: string } = {
          ...(ctx.optimisticReply as CommentRow),
          ...(real as any),
          user: (real as any)?.user ? (real as any).user : (ctx.optimisticReply as any).user,
          __ck: (ctx.optimisticReply as any).__ck
        }

        resolveReply(activeCommentsPostId, ctx.parentId, ctx.tempId, merged)
      },
      onError: (_err, _vars, ctx) => {
        if (!hasValidPostId) return
        if (ctx?.tempId && ctx?.parentId) {
          rollbackReply(activeCommentsPostId, ctx.parentId, ctx.tempId)
          setDisplayCount(c => Math.max(0, c - 1))
          bumpSelectedPostCommentCount(-1)
        }
        addToast('error', 'There was an error')
      }
    }
  )

const { deleteComment } = useDeleteComment({
  onSuccess: (_res: any, vars: { commentId: string; parentId: string; isReply: boolean }) => {
    if (!hasValidPostId) return

    removeCommentFromStore(activeCommentsPostId, vars.commentId)

    setDisplayCount(c => Math.max(0, c - 1))
    bumpSelectedPostCommentCount(-1)

    patchPostCommentCount(activeCommentsPostId, -1)

    setSelectedEntity(null)
    setOptionsActive(false)
  }
})

const onEmojiClick = (emojiObject: { emoji: string }) => {
  setNewComment(prev => `${prev}${emojiObject.emoji}`)
  setToggleEmojiPicker(false)
}


useEffect(() => {
  if (!commentsActive) return

  const viewport = document.querySelector<HTMLMetaElement>(
    'meta[name="viewport"]'
  )

  if (!viewport) return

  const previousContent = viewport.getAttribute('content') ?? ''

  viewport.setAttribute(
    'content',
    'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, interactive-widget=resizes-content'
  )

  return () => {
    viewport.setAttribute('content', previousContent)
  }
}, [commentsActive])

useEffect(() => {
  if (!commentsActive) return

  const el = commentsContainerRef.current
  if (!el) return

  const observer = new ResizeObserver(() => {
    requestAnimationFrame(() => {
      scrollToBottom()
      setIsAtTop(false)
    })
  })

  observer.observe(el)

  return () => {
    observer.disconnect()
  }
}, [commentsActive])

  // Close on outside click
  useEffect(() => {
    if (!commentsActive) return

    function handleClick(event: MouseEvent) {
       const target = event.target as HTMLElement


    // ignore clicks inside globally-mounted modals
    if (
      target.closest('.modal') ??
      target.closest('.confirmModal') ??
      target.closest('[role="dialog"]')
    ) {
      return
    }

      // close emoji picker if click is outside picker + emoji button
      if (
        toggleEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(target)
      ) {
        setToggleEmojiPicker(false)
      }

      // close full comments panel if click is outside panel
      if (panelRef.current && !panelRef.current.contains(target)) {
        setCommentsActive(false)
        setToggleEmojiPicker(false)
        setIsCommentReply(false)
        setSelectedEntity(null)
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [commentsActive, toggleEmojiPicker, setCommentsActive])

  return (
    <div className={`preview__comments__wrapper ${commentsActive ? ' preview__comments__wrapper--active' : ''}`}>
      <div ref={panelRef} className={`preview__comments${commentsActive ? ' preview__comments--active' : ''}`}>
        <div className='preview__comments__title'>
          <div className='preview__comments__title--top'>
            <p onClick={(e: any) => e.stopPropagation()}>
              {displayCount} {t(displayCount === 1 ? 'comment' : 'comments')}
            </p>
            <img
              onClick={() => {
                closeComments()
              }}
              src={AllIcons.preview_close}
              alt={t('closeComments')}
            />
          </div>

          {storeHasMore && isAtTop && (
            <div className="preview__comments__loadmore">
              <span onClick={handleLoadPreviousComments}>{t('loadPreviousComments')}</span>
            </div>
          )}

          {isSingleCommentLink && commentCount > 1 && (
            <span onClick={() => viewAllCommentsFn?.()}>{t('viewAllComments')}</span>
          )}
        </div>

        {!optionsActive && (
          <div className='preview__comments__add'>
            <div className='preview__comments__input'>
              {isCommentReply && selectedEntity?.owner && (
                <span className='preview__comments__mention'>
                  @{selectedEntity.owner}
                </span>
              )}

              <input
                ref={inputRef}
                type='text'
                value={newComment}
                autoComplete="off"
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t('Write Comment') + '...'}
              />

              <div
                ref={emojiButtonRef}
                className='preview__comments__input__emoji'
                onClick={() => setToggleEmojiPicker(v => !v)}
              >
                <img src={AllIcons.smile} alt={t('emojis')} />
              </div>

              {toggleEmojiPicker ? (
                <div ref={emojiPickerRef} className='emojiPicker'>
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      onEmojiClick({ emoji: emojiData.emoji })
                    }}
                    previewConfig={{
                      showPreview: false
                    }}
                    autoFocusSearch={false}
                    suggestedEmojisMode={SuggestionMode.RECENT}
                    lazyLoadEmojis
                    height={370} 
                    width={300} 
                    skinTonesDisabled
                    categories={[
                      {
                        category: Categories.SUGGESTED,
                        name: 'Recent'
                      },
                      {
                        category: Categories.SMILEYS_PEOPLE,
                        name: 'Smileys & People'
                      }
                    ]}
                  />
                </div>
              ) : null}
            </div>

            <div
              className='preview__comments__send'
              onClick={() => {
                if (userData.status === 'restricted') {
                  addToast('error', t('commentRestrictedAcc'))
                  return
                }
                if (!hasValidPostId) return
                if (!newComment) return

                if (isCommentReply) {
                  if (selectedEntity && !commentReply.isLoading) {
                    commentReply.mutate({ id: selectedEntity.parentId, reply: newComment })
                  }
                } else {
                  if (!newPostComment.isLoading) {
                    newPostComment.mutate(newComment)
                  }
                }
              }}
            >
              <img className='preview__comments__send--icon' src={AllIcons.button_send} alt={t('send')} />
            </div>
          </div>
        )}

        <div className='preview__comments__container' ref={commentsContainerRef} onScroll={handleContainerScroll}>
          {orderedComments.length === 0 && (
            <div className='preview__nocomments'>
              <img src={noCommentsImg} alt='no comments bg' />
              <h3>{t('noComments')}</h3>
            </div>
          )}

          {orderedComments.length > 0 &&
            orderedComments.map((comment: any, index: number) => {
              const isLast = index === orderedComments.length - 1
              return (
                <div
                  key={comment.__ck || comment.id}
                  data-id={comment.id}
                  className={isLast ? 'preview__commentItem preview__commentItem--last' : 'preview__commentItem'}
                >
              <CommentBlock
                ref={inputRef}
                data={comment}
                refetchComments={() => {}}
                selectedEntity={selectedEntity}
                isCommentReply={isCommentReply}
                setIsCommentReply={setIsCommentReply}
                setSelectedEntity={setSelectedEntity}
                setInitialCommentLiked={() => {}}
                deleteComment={(commentId, parentId, isReply) => {
                  deleteComment.mutate({ commentId, parentId, isReply })
                }}
                isMyPost={isMyPost}
                optionsActive={optionsActive}
                setOptionsActive={setOptionsActive}
                startReply={startReply}
              />
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default PreviewComments
